import { QueryController, createCsrfToken, createHeaders, fetchJsonQuery } from "@mne-core/api";
import type { IResultstash, JsonQuery } from "@mne-core/api";

import AuthenticationURI from "./AuthenticationURIBuilder";

export interface IPrepareWebauthnAuthorizationOpts extends RequestInit {
  apiUrl: string;
  apiKey?: string;
  sessionKey: string;
  ctrl?: QueryController;
}

export interface IPrepareWebauthnAuthorizationResponse {
  challengeID: string; // ID of the challenge returned in this response
  challenge: string; // The actual challenge, which will be embedded in the webAuthn clientDataJSON
  permittedWebAuthnKeyIDs: Array<string>; // A list of WebAuthn key IDs which are permitted to use
  timeout: number; // The timeout to use when waiting for the user
}

export const createRequest = (opts: IPrepareWebauthnAuthorizationOpts): Request => {
  const { apiUrl, apiKey, sessionKey, ...restOpts } = opts;
  const uri = new AuthenticationURI().setApiUrl(apiUrl).setSegment(["signature", "webauthn", "authorize"]).build();

  const headers = createHeaders({
    "ARGUS-API-KEY": apiKey,
    Accept: "application/json",
    "Content-type": "application/json",
    "ARGUS-CSRF-Token": !apiKey ? createCsrfToken(sessionKey, String(uri)) : false,
  });

  let localRequestOpts: RequestInit = {
    headers,
    method: "GET",
    mode: "cors",
  };

  if (!apiKey) {
    /**
     * Only add cookies if no apikey is provided
     */
    localRequestOpts = {
      ...localRequestOpts,
      credentials: "include",
    };
  }

  return new Request(String(uri), {
    ...localRequestOpts,
    ...restOpts,
  });
};

async function prepareWebauthnAuthorization(
  opts: IPrepareWebauthnAuthorizationOpts,
): Promise<JsonQuery<IResultstash<IPrepareWebauthnAuthorizationResponse>>> {
  return await fetchJsonQuery<IResultstash<IPrepareWebauthnAuthorizationResponse>>(createRequest(opts), {
    ctrl: opts.ctrl || new QueryController(),
  });
}

export default prepareWebauthnAuthorization;
